import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Droplet } from 'lucide-react';

function formatDateTime(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return '—'; }
}

/**
 * Day 11 — Fluid Intake Trend Card (minimal, read-only)
 * Renders the server-computed `fluid_trend` block. Safe on null/empty data.
 */
export function FluidTrendCard({ trend }) {
  if (!trend) return null;
  const { latest_intake, last_24h_total_ml, last_7d_total_ml, last_7d_entry_count } = trend;
  return (
    <Card data-testid="fluid-trend-card" className="border-sky-200">
      <CardContent className="py-3 space-y-2">
        <div className="flex items-center gap-1.5 text-sm font-semibold text-sky-700">
          <Droplet className="w-4 h-4" />
          Fluid intake — trend summary
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
          <div className="rounded-md border bg-sky-50/60 px-3 py-2" data-testid="fluid-trend-24h">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last 24h</div>
            <div className="text-base font-semibold text-sky-800">{last_24h_total_ml ?? 0} mL</div>
          </div>
          <div className="rounded-md border bg-sky-50/60 px-3 py-2" data-testid="fluid-trend-7d-total">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Last 7 days</div>
            <div className="text-base font-semibold text-sky-800">{last_7d_total_ml ?? 0} mL</div>
          </div>
          <div className="rounded-md border bg-sky-50/60 px-3 py-2" data-testid="fluid-trend-7d-count">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Entries (7d)</div>
            <div className="text-base font-semibold text-sky-800">{last_7d_entry_count ?? 0}</div>
          </div>
          <div className="rounded-md border bg-sky-50/60 px-3 py-2" data-testid="fluid-trend-latest">
            <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Latest</div>
            {latest_intake ? (
              <div className="text-xs text-sky-800">
                <div className="font-semibold">{latest_intake.amount_ml ?? '—'} mL · {latest_intake.fluid_type || '—'}</div>
                <div className="text-[10px] text-muted-foreground">{formatDateTime(latest_intake.intake_time)}</div>
                {latest_intake.intake_method && (
                  <Badge variant="outline" className="mt-1 text-[10px] capitalize">{latest_intake.intake_method}</Badge>
                )}
              </div>
            ) : <div className="text-xs italic text-muted-foreground">No entries yet</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default FluidTrendCard;
